
import React from 'react';
import { format, addDays, differenceInDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';

interface PeriodDateDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  activeCalendar: 'start' | 'end';
  setActiveCalendar: (calendar: 'start' | 'end') => void;
  onSave: () => void;
}

export const PeriodDateDialog: React.FC<PeriodDateDialogProps> = ({
  isOpen,
  onOpenChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  activeCalendar,
  setActiveCalendar,
  onSave
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="text-hormony-charcoal">✨ Edit Your Period Dates</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-4 py-4">
          <div className="flex justify-center space-x-4 w-full mb-2">
            <Button 
              variant={activeCalendar === 'start' ? "default" : "outline"}
              onClick={() => setActiveCalendar('start')}
              className={`flex-1 ${activeCalendar === 'start' ? 'bg-hormony-coral hover:bg-hormony-dark-coral' : 'border-hormony-beige text-hormony-charcoal hover:bg-hormony-cream/30'}`}
            >
              Start Date
            </Button>
            <Button 
              variant={activeCalendar === 'end' ? "default" : "outline"}
              onClick={() => setActiveCalendar('end')}
              className={`flex-1 ${activeCalendar === 'end' ? 'bg-hormony-coral hover:bg-hormony-dark-coral' : 'border-hormony-beige text-hormony-charcoal hover:bg-hormony-cream/30'}`}
            >
              End Date
            </Button>
          </div>
          
          <p className="text-center text-sm text-muted-foreground">
            {activeCalendar === 'start' ? 
              '📅 Select the first day of your period' : 
              '📅 Select the last day of your period'}
          </p>
          
          <div className="border rounded-md p-1 w-full border-hormony-beige/50">
            <Calendar
              mode="single"
              selected={activeCalendar === 'start' ? startDate : endDate}
              onSelect={activeCalendar === 'start' ? setStartDate : setEndDate}
              className="rounded-md border pointer-events-auto"
              initialFocus
              disabled={(date) => date > new Date()}
            />
          </div>
          
          {startDate && (
            <p className="text-sm font-medium text-hormony-charcoal">
              Start Date: {format(startDate, 'MMMM d, yyyy')}
            </p>
          )}
          
          {endDate && (
            <p className="text-sm font-medium text-hormony-charcoal">
              End Date: {format(endDate, 'MMMM d, yyyy')}
            </p>
          )}
          
          {startDate && endDate && (
            <p className="text-sm text-hormony-coral font-medium">
              Period Length: {differenceInDays(endDate, startDate) + 1} days
            </p>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-hormony-beige text-hormony-charcoal hover:bg-hormony-cream/30">
            Cancel
          </Button>
          <Button 
            className="bg-hormony-coral hover:bg-hormony-dark-coral text-white"
            onClick={onSave}
            disabled={!startDate}
          >
            💕 Save Period Data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
